import { Contract } from "@/services/backend/rlt/contracts/types/contract";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { useState } from "react";
import {
  UpdateLifetimeRequest,
  UpdatePaymentTermsRequest,
  UpdateSpecialAgreementsRequest,
  updateRltContractCache,
  useRltContractResetReferenceNumberMutation,
  useRltContractUpdateLifetimeMutation,
  useRltContractPatchMutation,
  useRltContractUpdatePaymentTermsMutation,
  useRltContractUpdateSpecialAgreementsMutation,
} from "@/services/backend/rlt/contracts/api/contract-api";
import { ReferenceNumberField } from "@/services/backend/documents/components/reference-number-field";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { Matcher } from "react-day-picker";
import { Label } from "@/shared/components/ui/label";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
import { ChevronDown } from "lucide-react";
import { Key } from "@/services/backend/rlt/defaults/types/defaults";
import { Contract as ContractDefaults } from "@/services/backend/rlt/defaults/types/contract";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Badge } from "@/shared/components/ui/badge";
import { useRltDefaultsShowQuery } from "@/services/backend/rlt/defaults/api/defaults-api";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { TextInputField } from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";

export function ContractBaseData({ contract }: { contract: Contract }) {
  const patchReferenceNumber = useRltContractPatchMutation();
  const resetReferenceNumber = useRltContractResetReferenceNumberMutation();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex space-x-4">
          <span>{t("Vertrag")}</span>
          <Badge>{t(contract.contractState)}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <ReferenceNumberField
            data={contract}
            updateCache={updateRltContractCache}
            patchMutation={patchReferenceNumber}
            resetMutation={resetReferenceNumber}
            disabled={isImmutable(contract)}
          />
          <ContractLifetime contract={contract} />
          <PaymentTerms contract={contract} />
          <SpecialAgreements contract={contract} />
        </div>
      </CardContent>
    </Card>
  );
}

function ContractLifetime({ contract }: { contract: Contract }) {
  const [request, setRequest] = useState<UpdateLifetimeRequest>({
    id: contract.id,
    lifetime: contract.lifetime,
  });
  const mutation = useRltContractUpdateLifetimeMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: contract.id,
    lifetime: contract.lifetime,
  });
  const { resetField } = useErrValidationFailedContext();

  const endDateRestrictions: Matcher = request.lifetime.unlimited
    ? true
    : { before: new Date(request.lifetime.startDate) };

  return (
    <div className="grid grid-cols-2 items-start gap-4">
      <DatePickerField
        label={t("Begin des Vertrages")}
        nullable={false}
        date={request.lifetime.startDate}
        onDateChange={(startDate) =>
          setRequest({
            ...request,
            lifetime: { ...request.lifetime, startDate },
          })
        }
        fieldName="lifetime.startDate"
        disabled={isImmutable(contract)}
        captionLayout="dropdown"
      />
      <div className="grid items-center gap-1.5">
        <DatePickerField
          label={t("Ende des Vertrages")}
          nullable
          date={request.lifetime.endDate ?? undefined}
          onDateChange={(endDate) =>
            setRequest({
              ...request,
              lifetime: { ...request.lifetime, endDate: endDate ?? null },
            })
          }
          fieldName="lifetime.endDate"
          disabled={endDateRestrictions}
          captionLayout="dropdown"
        />
        <div className="flex items-center gap-1.5">
          <Checkbox
            checked={request.lifetime.unlimited}
            onCheckedChange={(checked) => {
              const unlimited = Boolean(checked);
              if (typeof unlimited === "undefined") return;

              // An unlimited contract has no end date to be missing.
              resetField("lifetime.endDate");
              setRequest({
                ...request,
                lifetime: {
                  ...request.lifetime,
                  endDate: null,
                  unlimited,
                },
              });
            }}
            disabled={isImmutable(contract)}
          />
          <Label>{t("Unbegrenzte Laufzeit")}</Label>
        </div>
      </div>
    </div>
  );
}

function PaymentTerms({ contract }: { contract: Contract }) {
  const [request, setRequest] = useState<UpdatePaymentTermsRequest>({
    id: contract.id,
    terms: contract.paymentTerms,
  });
  const mutation = useRltContractUpdatePaymentTermsMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: contract.id,
    terms: contract.paymentTerms,
  });
  const { resetField } = useErrValidationFailedContext();

  return (
    <div className="flex w-full items-end space-x-4">
      <TextInputField
        label={t(
          "Zahlungsbedingungen (Formulierung für Paragraph 5 im Vertrag)",
        )}
        text={request.terms}
        onTextChange={(terms) => setRequest({ ...request, terms })}
        fieldName="terms"
        debounce={false}
        disabled={isImmutable(contract)}
      />
      <PaymentTermsPicker
        onSelect={(terms) => {
          resetField("terms");
          setRequest({ ...request, terms });
        }}
        disabled={isImmutable(contract)}
      />
    </div>
  );
}

function PaymentTermsPicker({
  onSelect,
  disabled = undefined,
}: {
  onSelect: (term: string) => void;
  disabled?: boolean;
}) {
  const { data, error } = useRltDefaultsShowQuery({
    key: Key.Contract,
  });

  const defaultsData = data?.data as ContractDefaults;
  const paymentTerms = defaultsData?.paymentTerms ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" disabled={disabled}>
          <span>{t("Aus Standard übernehmen")}</span>
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <RTKQueryErrorAlert error={error} />
        <DropdownMenuGroup>
          {paymentTerms.map((term) => (
            <DropdownMenuItem key={term} onSelect={() => onSelect(term)}>
              {term}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SpecialAgreements({ contract }: { contract: Contract }) {
  const [request, setRequest] = useState<UpdateSpecialAgreementsRequest>({
    id: contract.id,
    agreements: contract.specialAgreements,
  });
  const mutation = useRltContractUpdateSpecialAgreementsMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: contract.id,
    agreements: contract.specialAgreements,
  });

  return (
    <TextareaField
      label={t(
        "Besondere Vereinbarungen (OPTIONAL: Formulierung für Paragraph 8 im Vertrag)",
      )}
      value={request.agreements}
      onChange={(e) => setRequest({ ...request, agreements: e.target.value })}
      fieldName="agreements"
      debounce={false}
      disabled={isImmutable(contract)}
    />
  );
}
